import { useCallback, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import styles from './TeamMembers.module.scss';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import { useTeamsSummary } from '../../../../lib/api/swrApi';
import { COMMON_LABELS, TEAM_MANAGMENT_LABELS } from '../../../../lib/constants/labelKeys';
import useDebouncedCallback from '../../../../lib/hooks/useDebouncedCallback';
import { DEFAULT_DEBOUNCE_TIME } from '../../../../lib/constants/globals';
import { TeamMemberSortValue, TeamMembersProps } from './TeamMembers.types';
import TeamAccordionItem from '../../base/TeamAccordionItem/TeamAccordionItem';
import { SearchAndSort } from '../../base/SearchAndSort/SearchAndSort';
import globalStyles from '../../../../styles/exported.module.scss';
import TeamMembersSkeleton from './TeamMembersSkeleton';
import RefreshLoader from '../../../common/RefreshLoader/RefreshLoader';
import ErrorInfo from '../../base/ErrorInfo/ErrorInfo';
import HeadingWithIcon from '../../../common/HeadingWithIcon/HeadingWithIcon';
import TeamMembersContext from '../../../../lib/context/TeamMembersContext';
import useFeature from '../../../../lib/hooks/useFeature';
import { FeatureToggleCodename } from '../../../../lib/types/enums';

const teamMembersSortValues: { value: TeamMemberSortValue; labelKey: string }[] = [
  { value: 'overdue', labelKey: TEAM_MANAGMENT_LABELS.searchAndSort.OVERDUE_FIRST },
  { value: 'name|asc', labelKey: COMMON_LABELS.SEARCH.SORT },
  { value: 'name|desc', labelKey: COMMON_LABELS.SEARCH.SORT },
];

const TeamMembers = ({ heading, headingLevel, icon, ...rest }: TeamMembersProps) => {
  const [searchValue, setSearchValue] = useState('');
  const hideOverdue = useFeature(FeatureToggleCodename.hideOverdueDates);
  const [sortValue, setSortValue] = useState<TeamMemberSortValue>(hideOverdue ? 'name|asc' : 'overdue');
  const { data, mutate: refreshTeamsSummary, isLoading, isValidating, error } = useTeamsSummary();
  const [itemExpanded, setItemExpanded] = useState<string | null>(null);
  const sortValues = teamMembersSortValues.filter(item => !hideOverdue || item.value !== 'overdue');

  const teamsData = useMemo(() => {
    if (!data) {
      return [];
    }

    return data
      .filter(team => team.id && team.teamPathNames?.toLowerCase().includes(searchValue.toLowerCase()))
      .sort((a, b) => {
        const { totalOverdue: aTotalOverdue = 0, teamPathNames: aTeamPathNames = '0' } = a;
        const { totalOverdue: bTotalOverdue = 0, teamPathNames: bTeamPathNames = '0' } = b;

        switch (sortValue) {
          case 'overdue':
            return bTotalOverdue - aTotalOverdue;
          case 'name|asc':
            return bTeamPathNames < aTeamPathNames ? 1 : -1;
          default:
            return bTeamPathNames > aTeamPathNames ? 1 : -1;
        }
      });
  }, [data, searchValue, sortValue]);

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortValue(event.target.value as TeamMemberSortValue);
    setItemExpanded(null);
  };

  const handleSearchChange = useDebouncedCallback((phrase: string) => {
    setSearchValue(phrase);
    setItemExpanded(null);
  }, DEFAULT_DEBOUNCE_TIME);

  const handleItemClick = useCallback((teamId: string) => {
    setItemExpanded(prev => {
      if (prev === teamId) {
        return null;
      }

      return teamId;
    });
  }, []);

  if (error) {
    return <ErrorInfo error={error} />;
  }

  return (
    <div className={globalStyles.fullTabsWidth}>
      <HeadingWithIcon level={HeadingLevel.h2} as={headingLevel} icon={icon} classNames={{ container: styles.heading }}>
        {heading}
      </HeadingWithIcon>
      <SearchAndSort
        className={styles.sortContainer}
        onSearchChange={handleSearchChange}
        onSortChange={handleSortChange}
        labels={{
          searchPlaceholder: COMMON_LABELS.SEARCH.TEAM,
          sortTeamsBy: TEAM_MANAGMENT_LABELS.searchAndSort.SORT_TEAMS_BY,
        }}
        activeSortValue={sortValue}
        sortValues={sortValues}
      />
      <TeamMembersContext value={{ ...rest, onClick: handleItemClick, refreshTeamsSummary }}>
        <ul className={styles.accordionsList}>
          {isValidating && !isLoading && <RefreshLoader />}
          {isLoading ? (
            <TeamMembersSkeleton />
          ) : (
            teamsData.map(({ id = '', ...restTeamProps }) => (
              <TeamAccordionItem
                className={styles.accordionItem}
                key={id}
                teamMeta={{ id, ...restTeamProps }}
                membersCount={restTeamProps.totalMembers ?? 0}
                overdue={restTeamProps.totalOverdue}
                isExpanded={itemExpanded === id}
              />
            ))
          )}
        </ul>
      </TeamMembersContext>
    </div>
  );
};
export default TeamMembers;
